import { AUDIO_TUNABLES } from '../../platform/config/audio';
import { DEFAULT_CAMERA_MODE, DEFAULT_JUMP_SUBSTITUTE } from '../../platform/config/camera-mode';
import type { CameraMode } from '../../shared/types/camera-mode';
import type {
  SecondPlayerLaunchConfig,
  SessionLaunchConfig,
} from '../../shared/types/session-launch';
import type { ChildProfile, FamilyProfile } from '../../shared/types/profiles';
import type { ThriveRepositories } from '../../storage/interfaces/repositories';
import type { ActivePlayer } from '../shell/services-context';

/**
 * Where a session's starting settings come from.
 *
 * Narration, volume and reduced motion each have two persisted homes — the family's
 * `ParentSettings` and the child's own `accessibility` block — and nothing reconciled them.
 * The rule, settled for Slice D: **the child's own preference wins, field by field; the family
 * setting is the fallback; a built-in default is the floor.** Field by field rather than
 * whole-object, because a child profile that only ever set `reducedMotion` should not silently
 * discard the family's narration choice.
 *
 * This is a read. The setup screen seeds itself from here and never writes back — profile
 * editing belongs on the profiles and parent screens, not on a per-session launch form.
 */

export const BUILT_IN_LAUNCH_DEFAULTS: SessionLaunchConfig = {
  playerMode: 'SINGLE',
  band: 'explorer',
  narrationEnabled: true,
  narrationVolume: 1,
  soundEnabled: true,
  soundVolume: AUDIO_TUNABLES.defaultSoundVolume,
  reducedMotion: false,
  useCuratedSequence: true,
  showParentSummaryAfterMission: true,
  cameraMode: DEFAULT_CAMERA_MODE,
  jumpSubstitute: DEFAULT_JUMP_SUBSTITUTE,
};

export interface LaunchDefaultsInput {
  activePlayer: ActivePlayer | null;
  /** The active player's stored profile, or null for a guest. */
  child: ChildProfile | null;
  family: FamilyProfile | null;
  /**
   * What the readiness check measured about this room, if it ran this sitting (D-059).
   *
   * Deliberately *not* part of the stored precedence chain: it is neither a child's preference nor
   * a family's, it is a measurement of where somebody was standing five minutes ago. It seeds the
   * field and the parent decides. Absent — nobody ran the camera check — falls back to the flagged
   * Full-Motion default rather than guessing that a small room is a small room.
   */
  recommendedCameraMode?: CameraMode | null;
}

export function resolveSessionLaunchDefaults(input: LaunchDefaultsInput): SessionLaunchConfig {
  const child = input.child?.accessibility;
  const family = input.family?.parentSettings;
  const label = input.activePlayer?.label ?? input.child?.nickname;

  return {
    // One player unless a grown-up asks for two on the setup screen, and only there. The stored
    // `defaultPlayerMode` is deliberately not consulted: a second hero has to be a second *child*
    // somebody named for this sitting, and a deep link that skipped setup has named nobody.
    playerMode: BUILT_IN_LAUNCH_DEFAULTS.playerMode,
    band: input.child?.band ?? input.activePlayer?.band ?? BUILT_IN_LAUNCH_DEFAULTS.band,
    ...(label === undefined ? {} : { playerLabel: label }),
    narrationEnabled:
      child?.narrationEnabled ??
      family?.narrationEnabled ??
      BUILT_IN_LAUNCH_DEFAULTS.narrationEnabled,
    narrationVolume:
      child?.narrationVolume ?? family?.narrationVolume ?? BUILT_IN_LAUNCH_DEFAULTS.narrationVolume,
    reducedMotion:
      child?.reducedMotion ?? family?.reducedMotion ?? BUILT_IN_LAUNCH_DEFAULTS.reducedMotion,
    /**
     * Family-level only, and **absent means the default** (PS5-5).
     *
     * `ParentSettings.soundEnabled`/`soundVolume` are optional precisely so that a record written
     * before this build reads as "sound on, gently" rather than as silence or as `undefined`. This
     * is the single place that turns the absence into a value, which is what keeps the no-migration
     * rule honest — there is nothing to migrate because nothing downstream ever sees the gap.
     */
    soundEnabled: family?.soundEnabled ?? BUILT_IN_LAUNCH_DEFAULTS.soundEnabled,
    soundVolume: family?.soundVolume ?? BUILT_IN_LAUNCH_DEFAULTS.soundVolume,
    useCuratedSequence: BUILT_IN_LAUNCH_DEFAULTS.useCuratedSequence,
    // Family-level only: it is a preference about what a grown-up is shown, so a child profile
    // has no equivalent field to win the field-by-field precedence with.
    showParentSummaryAfterMission:
      family?.showParentSummaryAfterMission ??
      BUILT_IN_LAUNCH_DEFAULTS.showParentSummaryAfterMission,
    // Readiness recommends, the parent chooses (D-059). Neither profile layer holds an opinion,
    // because neither is about a room.
    cameraMode: input.recommendedCameraMode ?? BUILT_IN_LAUNCH_DEFAULTS.cameraMode,
    jumpSubstitute: BUILT_IN_LAUNCH_DEFAULTS.jumpSubstitute,
  };
}

/**
 * The second hero's starting settings, seeded from their own profile (PS2-7, MVP notes §7:
 * "Parents may still select a starting band for each child profile").
 *
 * Same read-only rule as the first hero's: this seeds a form, and the choice that comes back is
 * session-only. Narration, volume and motion are properties of the room rather than of a child,
 * so they stay single-valued for the sitting — only the band and the identity are per-hero.
 */
export function resolveSecondPlayerDefaults(child: ChildProfile | null): SecondPlayerLaunchConfig {
  if (child === null) return { band: BUILT_IN_LAUNCH_DEFAULTS.band };

  return {
    profileId: child.id,
    label: child.nickname,
    band: child.band,
    // The generation this second hero was chosen at (P6R17-1). Carried, never re-read.
    profileCreatedAt: child.createdAt,
  };
}

/**
 * The same rule, reading both layers through the repositories.
 *
 * A guest has no profile by definition (D-025), so no child lookup happens for one — which also
 * stops a stored child's preferences leaking into a guest's session.
 */
export async function loadSessionLaunchDefaults(
  repositories: ThriveRepositories,
  activePlayer: ActivePlayer | null,
  recommendedCameraMode: CameraMode | null = null,
): Promise<SessionLaunchConfig> {
  const profileId = activePlayer?.profileId;
  const [family, child] = await Promise.all([
    repositories.profiles.getFamily(),
    profileId === undefined
      ? Promise.resolve<ChildProfile | null>(null)
      : repositories.profiles.getChild(profileId),
  ]);

  return resolveSessionLaunchDefaults({ activePlayer, child, family, recommendedCameraMode });
}
